import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeDollarSign, CalendarClock, Gift, Link2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money } from "@/lib/api";
import { Loading } from "@/components/Loading";
import { BigCheck } from "@/components/wizard/Stepper";

const PERKS = [
  [Link2, "Your own link and code", "Share a link like voices2remember.com/?ref=yourname, or give people your code for checkout."],
  [BadgeDollarSign, "$5 for every paid order", "Earn $5 for each paid order you refer, no matter how many items are in it."],
  [CalendarClock, "Paid monthly", "Commissions are held 30 days, then paid once a month when you reach $25."],
  [Gift, "No discount needed", "Your code doesn't change the price. Families pay the same and you still get credit."],
];

const Field = ({ id, label, hint, ...p }) => (
  <div>
    <label className="label" htmlFor={id}>{label}</label>
    {hint && <p className="-mt-1 mb-2 text-base text-[#5C4A3E]">{hint}</p>}
    {p.textarea ? <textarea id={id} className="field min-h-[120px]" {...p} textarea={undefined} /> : <input id={id} className="field" {...p} />}
  </div>
);

const ApplyForm = ({ user, onDone }) => {
  const [f, setF] = useState({ name: user.name || "", phone: user.phone || "", payout_method: "", promote: "", website: "", agree_terms: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/affiliates/apply", f); toast.success("Application sent!"); onDone(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="paper-card space-y-6 p-6 sm:p-8" data-testid="affiliate-apply-form">
      <h2 className="font-serif text-3xl font-bold text-[#2C1E16]">Apply to become an affiliate</h2>
      <p className="text-lg text-[#5C4A3E]">Signed in as <b>{user.email}</b>. We'll email you when your application is reviewed.</p>
      <Field id="aff-name" label="Your name" value={f.name} onChange={set("name")} required data-testid="apply-name-input" />
      <Field id="aff-phone" label="Phone number" type="tel" value={f.phone} onChange={set("phone")} required data-testid="apply-phone-input" />
      <p className="rounded-xl bg-[#F2EBE1] p-4 text-lg text-[#4A3B32]" data-testid="apply-code-note">We'll assign your personal referral link and checkout code when your application is approved. You'll find them in your approval email and on this page.</p>
      <Field id="aff-payout" label="How should we pay you?" hint="For example: PayPal email, Venmo @name, or mail a check." value={f.payout_method} onChange={set("payout_method")} required data-testid="apply-payout-input" />
      <Field id="aff-promote" label="How will you share Voices 2 Remember?" hint="For example: my church group, my Facebook page, my senior center." textarea value={f.promote} onChange={set("promote")} required minLength={10} data-testid="apply-promote-input" />
      <Field id="aff-web" label="Website or social page (optional)" value={f.website} onChange={set("website")} data-testid="apply-website-input" />
      <BigCheck checked={f.agree_terms} onChange={(v) => setF({ ...f, agree_terms: v })} testid="apply-terms-checkbox">
        I agree to the affiliate terms: $5 per eligible paid order, held 30 days, paid monthly at $25 or more. Refunded or canceled orders don't earn a commission, and referring my own orders is reviewed.
      </BigCheck>
      <button className="btn-primary w-full !text-xl" disabled={busy || !f.agree_terms} data-testid="apply-submit-button">{busy ? "Sending..." : "Send my application"}</button>
    </form>
  );
};

const STATUS_LABEL = { pending: "Holding 30 days", review: "Under review", batched: "In this month's payout", paid: "Paid", void: "Canceled (refund)" };

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl border-2 border-[#E6DEC6] bg-white p-4 text-center" data-testid={testid}>
    <p className="font-serif text-3xl font-bold text-[#2C1E16]">{value}</p>
    <p className="mt-1 text-base text-[#5C4A3E]">{label}</p>
  </div>
);

const Dashboard = ({ aff, origin }) => {
  const link = `${origin}/?ref=${aff.code}`;
  const s = aff.stats;
  return (
    <div className="space-y-6" data-testid="affiliate-dashboard">
      <div className="paper-card space-y-4 p-6 sm:p-8" data-testid="affiliate-approved-card">
        <h2 className="font-serif text-3xl font-bold text-[#2E6930]">Your affiliate dashboard</h2>
        {!aff.active && <p className="rounded-xl bg-[#FEF3C7] p-3 text-lg" data-testid="affiliate-inactive-note">Your affiliate account is paused. New referrals won't count right now.</p>}
        <p className="text-lg">Your referral link: <b className="break-all" data-testid="my-affiliate-link">{link}</b></p>
        <p className="text-lg">Your checkout code: <b data-testid="my-affiliate-code">{aff.code}</b></p>
        <button className="btn-secondary" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="copy-affiliate-link-button">Copy my link</button>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Link visits" value={s.clicks} testid="aff-stat-clicks" />
        <Stat label="Paid orders" value={s.orders} testid="aff-stat-orders" />
        <Stat label="Waiting to be paid" value={money(s.waiting)} testid="aff-stat-waiting" />
        <Stat label="Paid to you" value={money(s.paid)} testid="aff-stat-paid" />
      </div>
      <div className="paper-card p-6" data-testid="affiliate-commissions">
        <p className="text-xl font-bold text-[#2C1E16]">Your recent commissions</p>
        <p className="mt-1 text-base text-[#5C4A3E]">Each is held 30 days, then paid monthly once you reach $25. Payout method: {aff.payout_method || "not set"}.</p>
        {aff.recent.length ? (
          <ul className="mt-4 divide-y divide-[#E6DEC6]">
            {aff.recent.map((c, i) => (
              <li key={i} className="flex flex-wrap justify-between gap-2 py-3 text-lg" data-testid={`aff-commission-${i}`}>
                <span>{new Date(c.created_at).toLocaleDateString()}</span><b>{money(c.amount)}</b><span className="text-[#5C4A3E]">{STATUS_LABEL[c.status] || c.status}</span>
              </li>
            ))}
          </ul>
        ) : <p className="mt-4 text-lg text-[#5C4A3E]" data-testid="aff-no-commissions">No commissions yet. Share your link to get started!</p>}
      </div>
    </div>
  );
};

const Status = ({ me }) => {
  const origin = window.location.origin;
  if (me.affiliate) return <Dashboard aff={me.affiliate} origin={origin} />;
  const a = me.application;
  return (
    <div className="paper-card space-y-3 p-6 sm:p-8" data-testid="affiliate-application-status">
      <h2 className="font-serif text-3xl font-bold text-[#2C1E16]">{a.status === "rejected" ? "Application not approved" : "Thanks! Your application is being reviewed"}</h2>
      <p className="text-lg text-[#5C4A3E]">Sent {new Date(a.created_at).toLocaleDateString()}</p>
      <p className="text-lg text-[#5C4A3E]">{a.status === "rejected" ? (a.reject_reason || "Reply to our email if you have questions.") : "We'll email you as soon as it's approved."}</p>
    </div>
  );
};

export default function BecomeAffiliate() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const load = () => api.get("/affiliates/me").then(({ data }) => setMe(data));
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const showStatus = me && (me.affiliate || (me.application && me.application.status !== "rejected"));
  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-8" data-testid="become-affiliate-page">
      <p className="font-script text-2xl text-[#A83232]">Share the gift of a voice</p>
      <h1 className="mt-2 text-4xl font-bold text-[#4A2A18] sm:text-5xl">Become an affiliate</h1>
      <p className="mt-4 text-lg text-[#5C4A3E]">Know families who would treasure a grandparent's voice? Tell them about Voices 2 Remember and earn $5 for every paid order.</p>
      <ul className="mt-10 grid gap-5 sm:grid-cols-2">
        {PERKS.map(([Icon, t, d]) => (
          <li key={t} className="paper-card flex gap-4 p-6"><Icon size={32} className="shrink-0 text-[#A83232]" /><div><p className="text-xl font-bold text-[#2C1E16]">{t}</p><p className="mt-1 text-lg text-[#5C4A3E]">{d}</p></div></li>
        ))}
      </ul>
      <div className="mt-10">
        {!user ? (
          <div className="paper-card p-6 text-center sm:p-8" data-testid="affiliate-signin-card">
            <p className="text-xl text-[#2C1E16]">Please sign in with Google to apply.</p>
            <Link to="/login?next=/affiliates" className="btn-primary mt-5 !text-xl" data-testid="affiliate-signin-button">Sign in to apply</Link>
          </div>
        ) : !me ? <Loading /> : showStatus ? <Status me={me} /> : <ApplyForm user={user} onDone={load} />}
      </div>
    </div>
  );
}
